import { create } from 'zustand';
import { logger } from '@/features/help/utils/logger';
import { FormStructure } from '../types/forms';
import { Signer } from '@/features/application/types/signers'; // Use the canonical Signer type

interface SignerState {
  signers: Signer[];
  currentSigner: number;
  allSignersComplete: boolean;
  currentSignerCompleted: boolean;
  recordStatus: string | null;
  signerShortId: string | null;
}

interface SignerActions {
  // Core methods
  setSigners: (signers: Signer[]) => void;
  addSigner: (signer: Omit<Signer, 'signer_id' | 'short_id'>) => void;
  removeSigner: (signerId: string) => void;
  updateSigner: (signerId: string, updates: Partial<Signer>) => void;
  getSignerById: (signerId: string) => Signer | undefined;
  getSignerByOrder: (orderIndex: number) => Signer | undefined;
  getOrderedSigners: () => Signer[];

  // State management methods
  setCurrentSigner: (signerIndex: number) => void;
  setAllSignersComplete: (complete: boolean) => void;
  setCurrentSignerCompleted: (completed: boolean) => void;
  setRecordStatus: (status: string | null) => void;
  setSignerShortId: (shortId: string | null) => void;

  // Initialization
  initializeFromApiPayload: (payload: any) => void;

  // Helper functions
  updateSignerPermissions: (
    signerIndex: number,
    structure: FormStructure | null
  ) => { viewableFields: string[]; editableFields: string[] };
}

type SignerStore = SignerState & SignerActions;

export const useSignerStore = create<SignerStore>((set, get) => ({
  // State
  signers: [],
  currentSigner: 1,
  allSignersComplete: false,
  currentSignerCompleted: false,
  recordStatus: null,
  signerShortId: null,

  // Core methods
  setSigners: (signers: Signer[]) => {
    set({
      signers: signers.sort((a, b) => a.signer_index - b.signer_index),
    });
  },

  addSigner: (signerData) => {
    // This is a simplified version for local updates; backend creates the real signer
    const newSigner: Signer = {
      ...signerData,
      signer_id: `local-${Date.now()}`,
      short_id: '',
    };
    set((state) => ({
      signers: [...state.signers, newSigner].sort((a, b) => a.signer_index - b.signer_index),
    }));
  },

  removeSigner: (signerId: string) => {
    set((state) => ({
      signers: state.signers.filter((s) => s.signer_id !== signerId),
    }));
  },

  updateSigner: (signerId: string, updates: Partial<Signer>) => {
    set((state) => ({
      signers: state.signers.map((s) => (s.signer_id === signerId ? { ...s, ...updates } : s)),
    }));
  },

  getSignerById: (signerId: string) => {
    return get().signers.find((s) => s.signer_id === signerId);
  },

  getSignerByOrder: (orderIndex: number) => {
    return get().signers.find((s) => s.signer_index === orderIndex);
  },

  getOrderedSigners: () => {
    // Already sorted on set, just return
    return get().signers;
  },

  // State management methods
  setCurrentSigner: (signerIndex: number) => {
    logger.debug('Signer Store', 'Setting current signer', { signerIndex });
    set({ currentSigner: signerIndex });
  },

  setAllSignersComplete: (complete: boolean) => {
    set({ allSignersComplete: complete });
  },

  setCurrentSignerCompleted: (completed: boolean) => {
    set({ currentSignerCompleted: completed });
  },

  setRecordStatus: (status: string | null) => {
    set({ recordStatus: status });
  },

  setSignerShortId: (shortId: string | null) => {
    set({ signerShortId: shortId });
  },

  // Initialization
  initializeFromApiPayload: (payload: any) => {
    const { recordStatus = null, currentSignerIndex = 1, signerShortId = null } = payload;

    set({
      signers: [],
      allSignersComplete: false,
      currentSignerCompleted: false,
      recordStatus,
      currentSigner: currentSignerIndex,
      signerShortId,
    });
  },

  // Helper functions
  updateSignerPermissions: (signerIndex: number, structure: FormStructure | null) => {
    if (!structure?.pages) {
      return { viewableFields: [], editableFields: [] };
    }

    const viewableFields: string[] = [];
    const editableFields: string[] = [];

    structure.pages.forEach((page) => {
      page.sections.forEach((section) => {
        section.fields.forEach((field) => {
          if (field.permissions.canView.includes(signerIndex)) {
            viewableFields.push(field.id);
          }
          if (field.permissions.canEdit.includes(signerIndex)) {
            editableFields.push(field.id);
          }
        });
      });
    });

    logger.debug('Signer Store', 'Updated permissions for signer', {
      signerIndex,
      viewableFieldCount: viewableFields.length,
      editableFieldCount: editableFields.length,
    });

    return { viewableFields, editableFields };
  },
}));
